import { useEffect, useState } from 'react';
import {
  ShieldCheck,
  Mail,
  Phone,
  Bell,
  Webhook,
  Download,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { AppLayout } from '../components/AppLayout';
import { clearAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type ConsentStatus = 'granted' | 'revoked' | 'pending' | 'unknown';
type ConsentChannel = 'email' | 'sms' | 'push' | 'webhook';

interface Consent {
  id: string | null;
  channel: ConsentChannel;
  status: ConsentStatus;
  source: string | null;
  capturedAt: string | null;
  expiresAt: string | null;
}

// ─── Channel metadata ─────────────────────────────────────────────────────────

const CHANNEL_META: Record<
  ConsentChannel,
  { label: string; description: string; icon: React.ReactNode }
> = {
  email: {
    label: 'Email marketing',
    description: 'Receive newsletters, promotions and product updates via email.',
    icon: <Mail size={18} />,
  },
  sms: {
    label: 'SMS notifications',
    description: 'Receive text messages with offers, alerts and account updates.',
    icon: <Phone size={18} />,
  },
  push: {
    label: 'Push notifications',
    description: 'Receive browser or app push notifications.',
    icon: <Bell size={18} />,
  },
  webhook: {
    label: 'Third-party integrations',
    description: 'Allow us to share your data with integrated third-party services.',
    icon: <Webhook size={18} />,
  },
};

// ─── Toggle component ─────────────────────────────────────────────────────────

function ConsentToggle({
  consent,
  onToggle,
  busy,
}: {
  consent: Consent;
  onToggle: (channel: ConsentChannel, newStatus: 'granted' | 'revoked') => void;
  busy: boolean;
}) {
  const meta = CHANNEL_META[consent.channel];
  const isGranted = consent.status === 'granted';

  return (
    <div className="flex items-start justify-between gap-4 py-5">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div
          className={`mt-0.5 w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
            isGranted
              ? 'bg-[--color-primary]/10 text-[--color-primary]'
              : 'bg-zinc-100 text-zinc-400'
          }`}
        >
          {meta.icon}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">{meta.label}</p>
          <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{meta.description}</p>
          {consent.capturedAt && (
            <p className="text-xs text-zinc-400 mt-1">
              Last updated:{' '}
              {new Date(consent.capturedAt).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </p>
          )}
        </div>
      </div>

      {/* Toggle switch */}
      <button
        type="button"
        disabled={busy}
        onClick={() => onToggle(consent.channel, isGranted ? 'revoked' : 'granted')}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
          isGranted ? 'bg-[--color-primary]' : 'bg-zinc-200'
        } ${busy ? 'opacity-50 cursor-not-allowed' : ''}`}
        aria-checked={isGranted}
        role="switch"
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
            isGranted ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

// ─── Delete confirmation modal ────────────────────────────────────────────────

function DeleteAccountModal({
  onConfirm,
  onCancel,
  busy,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
            <AlertTriangle size={20} className="text-red-500" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-zinc-900">Delete your account</h3>
            <p className="text-sm text-zinc-500">This action cannot be undone</p>
          </div>
        </div>
        <p className="text-sm text-zinc-600 leading-relaxed">
          All your personal data will be anonymised and your account will be permanently
          deactivated. You will be signed out immediately. This action complies with GDPR Article
          17.
        </p>
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-xl border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Delete account
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PrivacyPage() {
  const navigate = useNavigate();
  const [consents, setConsents] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyChannel, setBusyChannel] = useState<ConsentChannel | null>(null);
  const [feedback, setFeedback] = useState<{ channel: ConsentChannel; ok: boolean } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<Consent[]>('/customer/consents')
      .then((res) => setConsents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Failed to load consent preferences.'))
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (channel: ConsentChannel, newStatus: 'granted' | 'revoked') => {
    setBusyChannel(channel);
    setFeedback(null);
    try {
      const res = await api.patch<Consent>(`/customer/consents/${channel}`, { status: newStatus });
      setConsents((prev) => prev.map((c) => (c.channel === channel ? { ...c, ...res.data } : c)));
      setFeedback({ channel, ok: true });
      setTimeout(() => setFeedback(null), 2500);
    } catch {
      setFeedback({ channel, ok: false });
      setTimeout(() => setFeedback(null), 3000);
    } finally {
      setBusyChannel(null);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/customer/data-export', { responseType: 'blob' });
      const blob = new Blob([res.data as BlobPart], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `my-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to generate data export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await api.post('/customer/account/delete', { confirm: true });
      clearAuth();
      navigate('/login', { replace: true });
    } catch {
      setError('Failed to delete account. Please contact support.');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-2xl space-y-8">
        <div>
          <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-[--color-primary]" />
            Privacy & Consents
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Control how we use your data and manage your communication preferences.
          </p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-5 py-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* ── Marketing consents ──────────────────────────────────────────── */}
        <section>
          <h2 className="text-base font-semibold text-zinc-900 mb-1">Marketing preferences</h2>
          <p className="text-sm text-zinc-500 mb-4">
            Choose which channels you're happy for us to use to contact you.
          </p>

          {loading ? (
            <div className="flex items-center gap-2 py-8 text-sm text-zinc-400">
              <Loader2 size={16} className="animate-spin" /> Loading preferences…
            </div>
          ) : (
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm divide-y divide-zinc-50 px-5">
              {consents.map((consent) => (
                <div key={consent.channel}>
                  <ConsentToggle
                    consent={consent}
                    onToggle={handleToggle}
                    busy={busyChannel === consent.channel}
                  />
                  {feedback?.channel === consent.channel && (
                    <div
                      className={`pb-3 -mt-2 flex items-center gap-1.5 text-xs font-medium ${
                        feedback.ok ? 'text-emerald-600' : 'text-red-500'
                      }`}
                    >
                      {feedback.ok ? (
                        <>
                          <CheckCircle size={12} /> Preference saved
                        </>
                      ) : (
                        <>
                          <AlertTriangle size={12} /> Failed to save — try again
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── GDPR rights ────────────────────────────────────────────────── */}
        <section>
          <h2 className="text-base font-semibold text-zinc-900 mb-1">Your data rights</h2>
          <p className="text-sm text-zinc-500 mb-4">
            Under GDPR you have the right to access and delete your personal data.
          </p>

          <div className="space-y-3">
            {/* Data portability */}
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Download size={18} className="text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900">Download your data</p>
                <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                  Export a copy of all personal data we hold about you in JSON format (GDPR Art.
                  20).
                </p>
              </div>
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100 transition-colors disabled:opacity-50 flex-shrink-0"
              >
                {exporting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                Export
              </button>
            </div>

            {/* Right to erasure */}
            <div className="bg-white border border-zinc-100 rounded-2xl shadow-sm p-5 flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} className="text-red-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900">Delete my account</p>
                <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
                  Permanently delete your account and anonymise all personal data (GDPR Art. 17).
                  This action cannot be undone.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors flex-shrink-0"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        </section>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowDeleteModal(false)}
          busy={deleting}
        />
      )}
    </AppLayout>
  );
}
